import { createContext, useCallback, useContext, useState, type ReactNode } from 'react'
import type { Repo } from '../types/git.types'
import { setActiveGitConnectionId } from '../lib/apiClient'

// ── Persistence ───────────────────────────────────────────────────────────────

const ACTIVE_CONN_KEY = 'stride.activeGitConnectionId'

function readPersistedActiveConnectionId(): string | null {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage.getItem(ACTIVE_CONN_KEY)
  } catch {
    return null
  }
}

function persistActiveConnectionId(id: string | null): void {
  if (typeof window === 'undefined') return
  try {
    if (id === null) window.localStorage.removeItem(ACTIVE_CONN_KEY)
    else window.localStorage.setItem(ACTIVE_CONN_KEY, id)
  } catch {
    // ignore storage failures (private mode / quota) — state still lives in memory
  }
}

// ── State shape ───────────────────────────────────────────────────────────────

interface AppState {
  selectedRepo:          Repo | null
  setSelectedRepo:       (repo: Repo | null) => void
  selectedBranch:        string
  setSelectedBranch:     (branch: string) => void
  /** id of the active git Connection — single source of truth for git scope. */
  activeConnectionId:    string | null
  setActiveConnectionId: (id: string | null) => void
  /**
   * Bumped whenever the set of connections changes. Put it in a `listConnections` effect's deps and
   * that effect re-runs when one is added, rotated or removed.
   *
   * ── Why this exists ──
   * Reported by Kunal: he connected GitHub and a Salesforce org, and the Repository section still said
   * "Connect a repo". Closing the app and reopening it fixed it.
   *
   * Every consumer fetched connections in `useEffect(..., [connSvc])`, and the service instance is
   * stable for the life of the app — so the fetch ran once, at mount, and nothing ever told it the
   * answer had changed. Pages that unmount on navigation got away with it by refetching on the way
   * back. The SHELL does not unmount, so the sidebar's repo switcher kept its first answer until the
   * process restarted, which is exactly the symptom.
   *
   * A counter rather than the connection list itself: the consumers each want a different slice (git
   * only, salesforce only, healthy only), and centralising the list would mean centralising all of
   * those filters too. This leaves each fetch where it is and only tells it WHEN to run again.
   */
  connectionsVersion:    number
  /** Call after creating, rotating or deleting a connection. */
  refreshConnections:    () => void
}

// ── Context ───────────────────────────────────────────────────────────────────

const AppContext = createContext<AppState | null>(null)

// ── Provider ──────────────────────────────────────────────────────────────────

interface AppProviderProps {
  children: ReactNode
  /**
   * Pre-select a repo on mount — used in tests to skip ActiveRepoSync.
   * Omitted (undefined) in the real app → starts null and is populated by
   * ActiveRepoSync.  Passing `null` explicitly forces the empty state.
   */
  initialRepo?: Repo | null
}

export function AppProvider({ children, initialRepo }: AppProviderProps): JSX.Element {
  const [selectedRepo,   setSelectedRepo]   = useState<Repo | null>(
    initialRepo !== undefined ? initialRepo : null,
  )
  const [selectedBranch, setSelectedBranch] = useState<string>('main')

  const [activeConnectionId, setActiveConnectionIdState] = useState<string | null>(
    readPersistedActiveConnectionId,
  )

  function setActiveConnectionId(id: string | null): void {
    setActiveConnectionIdState(id)
    persistActiveConnectionId(id)
    setActiveGitConnectionId(id)   // keep apiClient's git-request threading in sync
  }

  const [connectionsVersion, setConnectionsVersion] = useState(0)
  // Stable, so adding it to a dependency array does not itself cause a re-run.
  const refreshConnections = useCallback((): void => { setConnectionsVersion((v) => v + 1) }, [])

  return (
    <AppContext.Provider
      value={{
        selectedRepo,
        setSelectedRepo,
        selectedBranch,
        setSelectedBranch,
        activeConnectionId,
        setActiveConnectionId,
        connectionsVersion,
        refreshConnections,
      }}
    >
      {children}
    </AppContext.Provider>
  )
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAppContext(): AppState {
  const ctx = useContext(AppContext)
  if (!ctx) throw new Error('useAppContext must be used within an AppProvider')
  return ctx
}
